/*
  Desktop nav dropdowns: hover intent (KAN-854).

  CSS :hover alone closes a dropdown the instant the cursor leaves its nav
  item. The cursor leaves it on the way to most categories: the panel spans
  the page while the nav link is narrow, so a diagonal path runs along the
  nav bar over the neighbouring items.

  This keeps the open item in an `is-open` class instead:
  - with nothing open, hovering an item opens it straight away;
  - moving onto another item switches menus straight away, unless the cursor
    is heading down into the open panel (the "menu aim" triangle check), in
    which case the open menu holds until the cursor turns away or rests for
    HOLD_DELAY;
  - leaving the item and its panel closes it after CLOSE_DELAY, restarted
    for as long as the cursor keeps heading down into the panel.

  Timings follow NN/g's guidance on exposing hidden content
  (https://www.nngroup.com/articles/timing-exposing-content/). Mouse only:
  touch and keyboard keep the existing tap and focus behaviour.
*/
(function () {
  const CLOSE_DELAY = 400;
  const HOLD_DELAY = 300;
  // Movement flatter than this (dy/dx) counts as travelling along the nav,
  // not heading down into the panel.
  const MIN_DOWN_SLOPE = 0.05;
  const TRAIL_LENGTH = 6;
  const TRAIL_MAX_AGE = 100;
  const ITEM_SELECTOR = '.header__nav-items > .header__nav-item';
  const DROPDOWN_SELECTOR = ':scope > .header__nav-dropdown';

  if (!matchMedia('(hover: hover)').matches) return;

  document.documentElement.classList.add('has-nav-hover-intent');

  let openItem = null;
  let closeTimer = null;
  let holdTimer = null;
  let pendingItem = null;
  const trail = [];
  let lastPoint = null;
  let point = null;

  const dropdownOf = (item) => item.querySelector(DROPDOWN_SELECTOR);

  function setOpen(item) {
    if (openItem && openItem !== item) openItem.classList.remove('is-open');
    openItem = item && dropdownOf(item) ? item : null;
    if (openItem) openItem.classList.add('is-open');
  }

  function clearPending() {
    clearTimeout(holdTimer);
    holdTimer = null;
    pendingItem = null;
  }

  // True when the latest movement points into the triangle between the
  // previous cursor position and the top corners of the open panel.
  function isHeadingIntoPanel() {
    if (!openItem || !lastPoint || !point) return false;

    const dx = point.x - lastPoint.x;
    const dy = point.y - lastPoint.y;
    if (dy <= 0 || dy < Math.abs(dx) * MIN_DOWN_SLOPE) return false;

    const panel = dropdownOf(openItem).getBoundingClientRect();
    // Only a cursor above the panel can be heading into it.
    const rise = panel.top - lastPoint.y;
    if (rise <= 0) return false;

    // Where the current heading meets the panel's top edge.
    const hitX = lastPoint.x + (dx / dy) * rise;
    return hitX >= panel.left && hitX <= panel.right;
  }

  function switchTo(item) {
    clearPending();
    setOpen(item);
  }

  function cancelClose() {
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  function scheduleClose() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      closeTimer = null;
      setOpen(null);
    }, CLOSE_DELAY);
  }

  function enter(item) {
    cancelClose();

    if (item === openItem) {
      clearPending();
      return;
    }

    if (!openItem || !openItem.isConnected || !isHeadingIntoPanel()) {
      switchTo(item);
      return;
    }

    pendingItem = item;
    restartHold();
  }

  function restartHold() {
    const item = pendingItem;
    clearTimeout(holdTimer);
    holdTimer = setTimeout(() => switchTo(item), HOLD_DELAY);
  }

  function leave(item) {
    if (item === pendingItem) clearPending();
    if (openItem) scheduleClose();
  }

  // Measure the heading over the last few moves, not one, so a pixel of hand
  // jitter doesn't flip it. Older points are dropped, but the last two always
  // stay, so a move that starts after a pause is measured from where the
  // cursor rested. Called from pointerover too, which fires before the
  // pointermove for the same position.
  function track(event) {
    const last = trail[trail.length - 1];
    if (last && last.x === event.clientX && last.y === event.clientY) return;

    const now = event.timeStamp;
    trail.push({ x: event.clientX, y: event.clientY, t: now });
    while (trail.length > 2 && (trail.length > TRAIL_LENGTH || now - trail[0].t > TRAIL_MAX_AGE)) {
      trail.shift();
    }
    lastPoint = trail[0];
    point = trail[trail.length - 1];
  }

  document.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    track(event);

    // Crossing header space between the nav item and its panel (at narrower
    // widths the nav wraps onto a second row): keep the menu while the
    // cursor is still heading down into it.
    if (closeTimer && isHeadingIntoPanel()) scheduleClose();

    if (!pendingItem || event.target.closest(ITEM_SELECTOR) !== pendingItem) return;
    if (isHeadingIntoPanel()) {
      restartHold();
    } else {
      switchTo(pendingItem);
    }
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    track(event);
    const item = event.target.closest(ITEM_SELECTOR);
    const from = event.relatedTarget?.closest?.(ITEM_SELECTOR);
    if (item && item !== from) enter(item);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = event.target.closest(ITEM_SELECTOR);
    const to = event.relatedTarget?.closest?.(ITEM_SELECTOR);
    if (item && item !== to) leave(item);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openItem) return;
    cancelClose();
    clearPending();
    setOpen(null);
  });
})();
